import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { useEvent, useUpdateEffect } from 'react-use';
import { Apollo } from 'src/gql/generated';

type UnsubmittedReview = NonNullable<
  Apollo.GetTaskUnsubmittedReviewQuery['task']
>['viewerUnsubmittedReview'];

/**
 * A reviewer's unsubmitted markup, loaded apart from the task query. Counting it reads every
 * workflow document's editor JSON, so the task page and panel render without waiting, and a
 * failed read does not fail the task.
 *
 * Refetches when they come back from a document: the task's tab pane is shown again
 * (React.Activity reruns this effect then, which is the point, and the refetch only reads) and
 * when the window regains focus.
 *
 * `review` is undefined until the read answers, including when it fails. Null means there is
 * nothing unsubmitted.
 */
export function useUnsubmittedReview(
  taskId: UUID,
  options: { skip: boolean }
): { review: UnsubmittedReview | undefined } {
  const { skip } = options;
  const { data, error, refetch } = Apollo.useGetTaskUnsubmittedReviewQuery({
    fetchPolicy: 'cache-and-network',
    skip,
    variables: { taskId },
  });
  const refetchReview = useCallback(() => {
    if (!skip) void refetch();
  }, [refetch, skip]);

  useUpdateEffect(refetchReview, [refetchReview]);
  useEvent('focus', refetchReview, window);

  // errorPolicy is 'all', so a failed document read still returns the task with this field null.
  if (skip) return { review: null };
  if (error || !data?.task) return { review: undefined };
  return { review: data.task.viewerUnsubmittedReview };
}
